import { apiService } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface Patient {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone_number: string;
  username: string;
  date_of_birth: string;
  gender: string;
  address: string;
  avatarUrl: string | null;
  rmrKcalDay: number;
  weight: number;
  termsAndConditionsAccepted: string | null;
  lastActivityAt: string | null;
  app_version: string | null;
  fcm_token: string | null;
  weeklyDigestEnabled?: boolean;
  insightsEnabled?: boolean;
  serviceStatus?: ServiceStatus;
  assignedProfessional?: AssignedProfessional | null;
  clinic?: Clinic | null;
}

export interface AssignedProfessional {
  id: number;
  name: string;
  email: string;
  role: string;
}

export interface Clinic {
  id: number;
  name: string;
}

export interface ServiceStatus {
  pushNotifications: boolean;
  agentEnabled: boolean;
  agentProactiveEnabled: boolean;
  sleepEnabled: boolean;
  insightsEnabled: boolean;
}

export interface CreatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phoneNumber?: string;
  password?: string;
  welcomeEmailType?: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword';
}

export interface UpdatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phone_number: string;
  address: string;
  weeklyDigestEnabled: boolean;
  insightsEnabled: boolean;
}

export interface PaginatedResponse {
  actualPage: number;
  count: number;
  perpage: number;
  results: Patient[];
  totalPages: number;
}

export type PatientActivityStatus = 'active' | 'inactive' | 'never';

export const getPatientActivityStatus = (patient: Patient): PatientActivityStatus => {
  if (!patient.lastActivityAt) return 'never';
  const lastActivity = new Date(patient.lastActivityAt);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return lastActivity < oneWeekAgo ? 'inactive' : 'active';
};

export const patientsService = {
  getPatients: async (page: number = 1, search: string = ''): Promise<PaginatedResponse> => {
    let url = `${API_BASE_URL}/clinic/patient?is_active=true&page=${page}`;
    if (search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  },

  getPatientById: async (id: string | number): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching patient with id ${id}:`, error);
      throw error;
    }
  },

  createPatient: async (data: CreatePatientData): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient`;
    try {
      const payload: any = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        dateOfBirth: data.dateOfBirth,
        rmrKcalDay: data.rmrKcalDay,
        weight: data.weight,
      };
      
      if (data.phoneNumber) {
        payload.phone_number = data.phoneNumber;
      }
      
      if (data.password) {
        payload.password = data.password;
      }
      
      if (data.welcomeEmailType) {
        payload.welcomeEmailType = data.welcomeEmailType;
      }
      
      const response = await apiService.post(url, payload);
      return response;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },

  updatePatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, data);
      return response;
    } catch (error) {
      console.error(`Error updating patient with id ${id}:`, error);
      throw error;
    }
  },

  // Partial update via PATCH — used for standalone field toggles (e.g.
  // insightsEnabled, weeklyDigestEnabled) without sending the full patient form.
  patchPatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.patch(url, data);
      return response;
    } catch (error) {
      console.error(`Error patching patient with id ${id}:`, error);
      throw error;
    }
  },

  updatePatientPassword: async (id: string | number, password: string): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, { password });
      return response;
    } catch (error) {
      console.error(`Error updating password for patient with id ${id}:`, error);
      throw error;
    }
  },

  sendWelcomeEmail: async (userId: number, welcomeEmailType: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword' | 'accessReminder'): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/welcome-email/`;
    try {
      await apiService.post(url, { welcomeEmailType });
    } catch (error) {
      console.error(`Error sending welcome email to user ${userId}:`, error);
      throw error;
    }
  },

  deactivatePatient: async (userId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/activation/`;
    try {
      await apiService.patch(url, { is_active: false });
    } catch (error) {
      console.error(`Error deactivating patient with userId ${userId}:`, error);
      throw error;
    }
  },
}; 